import React from 'react';
import TestResultDisplay from '../../common/TestResultDisplay';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

interface NutritionSummaryDisplayProps {
  responses: any;
  nutritionStatus?: string;  // 新增：营养状况评估状态
  className?: string;
}

const NutritionSummaryDisplay: React.FC<NutritionSummaryDisplayProps> = ({ 
  responses, 
  nutritionStatus,
  className = '' 
}) => {
  // 判断营养状况是否通过 - 基于传入的状态
  const nutritionPassed = nutritionStatus === 'PASS';
  
  // 从真实的responses数据中获取营养评估数据
  const getRealNutritionData = () => {
    // 方式1: 从直接字段获取
    if (responses?.weightLoss !== undefined || responses?.appetiteLoss !== undefined) {
      return {
        weightLoss: responses.weightLoss,
        appetiteLoss: responses.appetiteLoss,
        weightLossPresent: responses.weightLossPresent,
        appetiteLossPresent: responses.appetiteLossPresent
      };
    }
    
    // 方式2: 从testResults中获取rawUserAnswers
    if (responses?.testResults) {
      const weightTest = responses.testResults.find((test: any) => test.name === "体重减轻评估");
      const appetiteTest = responses.testResults.find((test: any) => test.name === "食欲减退评估");
      
      if (weightTest?.rawUserAnswers || appetiteTest?.rawUserAnswers) {
        return {
          weightLoss: weightTest?.rawUserAnswers?.weightLoss,
          appetiteLoss: appetiteTest?.rawUserAnswers?.appetiteLoss,
          weightLossPresent: weightTest?.rawUserAnswers?.weightLossPresent,
          appetiteLossPresent: appetiteTest?.rawUserAnswers?.appetiteLossPresent
        };
      }
    }
    
    // 方式3: 从domainResult中的testResults获取 (QuickTestResultPage的数据结构修复)
    // 这是关键修复：QuickTestResultPage应该传递完整的domainResult而不只是responses
    if ((responses as any)?.status && (responses as any)?.testResults) {
      const weightTest = (responses as any).testResults.find((test: any) => test.name === "体重减轻评估");
      const appetiteTest = (responses as any).testResults.find((test: any) => test.name === "食欲减退评估");
      
      if (weightTest?.rawUserAnswers || appetiteTest?.rawUserAnswers) {
        return {
          weightLoss: weightTest?.rawUserAnswers?.weightLoss,
          appetiteLoss: appetiteTest?.rawUserAnswers?.appetiteLoss,
          weightLossPresent: weightTest?.rawUserAnswers?.weightLossPresent,
          appetiteLossPresent: appetiteTest?.rawUserAnswers?.appetiteLossPresent
        };
      }
    }

    return null;
  };
  
  // 动态生成营养状况测试数据，基于真实数据
  const generateNutritionTestData = (): TestGroup[] => {
    const realData = getRealNutritionData();
    
    // 如果没有真实数据，只显示用户实际完成的评估项
    if (!realData) {
      return [
        {
          title: '营养状况评估',
          passed: nutritionPassed,
          items: [
            {
              name: '评估状态',
              passed: nutritionPassed,
              userAnswer: nutritionPassed ? '营养状况正常' : '营养状况需要关注',
              details: '数据不可用，基于系统评估结果'
            }
          ]
        }
      ];
    }

    const testGroups: TestGroup[] = [];
    
    // 体重变化评估 - 基于真实数据
    if (realData.weightLoss !== undefined) {
      testGroups.push({
        title: '体重变化评估',
        passed: !realData.weightLossPresent,
        items: [
          {
            name: '体重下降问题',
            passed: !realData.weightLossPresent,
            userAnswer: realData.weightLoss ? '是的，我的体重有下降' : '没有，我的体重没有下降',
            details: '问题：您最近3个月内体重是否下降超过3公斤？'
          }
        ]
      });
    }
    
    // 食欲状况评估 - 基于真实数据
    if (realData.appetiteLoss !== undefined) {
      testGroups.push({
        title: '食欲状况评估',
        passed: !realData.appetiteLossPresent,
        items: [
          {
            name: '食欲减退问题',
            passed: !realData.appetiteLossPresent,
            userAnswer: realData.appetiteLoss ? '是的，我的食欲有减退' : '没有，我的食欲正常',
            details: '问题：您最近是否出现食欲减退情况？'
          }
        ]
      });
    }

    // 如果没有任何真实测试数据，显示状态信息
    if (testGroups.length === 0) {
      testGroups.push({
        title: '营养状况评估',
        passed: nutritionPassed,
        items: [
          {
            name: '评估结果',
            passed: nutritionPassed,
            userAnswer: nutritionPassed ? '营养状况正常' : '营养状况需要关注',
            details: '基于WHO ICOPE营养评估标准'
          }
        ]
      });
    }

    return testGroups;
  };

  const testGroups = generateNutritionTestData();
  const passedCount = testGroups.filter(group => group.passed).length;
  const totalCount = testGroups.length;

  return (
    <TestResultDisplay
      title="营养状况评估结果"
      subtitle={`综合评估: ${passedCount}/${totalCount}项指标正常`}
      testGroups={testGroups}
      className={className}
    />
  );
};

export default NutritionSummaryDisplay;